// Light, Dark or Auto (follow the device), as on the website. The choice is kept in localStorage "gp-theme" and
// applied before paint by the inline script in index.html; this keeps the switches in step after that.
import { useEffect, useState } from "react";

export type Theme = "auto" | "light" | "dark";
const KEY = "gp-theme";
const EVENT = "gp-theme";

function read(): Theme {
  try { const t = localStorage.getItem(KEY); return t === "light" || t === "dark" ? t : "auto"; } catch { return "auto"; }
}

export function setTheme(t: Theme) {
  try { t === "auto" ? localStorage.removeItem(KEY) : localStorage.setItem(KEY, t); } catch { /* private mode: this visit only */ }
  if (t === "auto") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
  window.dispatchEvent(new Event(EVENT));
}

/** Dark right now, whether picked or following the device. */
export const isDark = () => document.documentElement.dataset.theme === "dark"
  || (!document.documentElement.dataset.theme && window.matchMedia("(prefers-color-scheme: dark)").matches);

export function useTheme(): Theme {
  const [t, setT] = useState(read);
  useEffect(() => {
    const on = () => setT(read());
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return t;
}

export function ThemeSwitch() {
  const t = useTheme();
  return (
    <div className="themesw" role="group" aria-label="Appearance">
      {(["auto", "light", "dark"] as const).map((v) => (
        <button key={v} type="button" aria-pressed={t === v} onClick={() => setTheme(v)}>{v === "auto" ? "Auto" : v === "light" ? "Light" : "Dark"}</button>
      ))}
    </div>
  );
}
